import CloseIcon from "@mui/icons-material/Close"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { useMemo, useState, type SyntheticEvent } from "react"
import type React from "react"
import { Link as RouterLink } from "react-router"
import { extraColors } from "../../utils/theme"
import { buildBiothreatSignalDetailPath } from "./useBiothreatSignalById"
import { formatNwssActiveDateRange } from "./biothreatActiveDateRange"
import { NwssStatBand } from "./NwssDrawerStats"
import { NwssDetectionHeatmap } from "./NwssDetectionHeatmap"
import { NwssEntityBreakdown } from "./NwssEntityBreakdownTable"
import { NwssRawTable } from "./NwssRawSamplesTable"
import { NwssRollingDetectionRateChart } from "./NwssRollingDetectionRateChart"
import { NwssSiteBubbleTimeline } from "./NwssSiteBubbleTimeline"
import {
  computeNwssSamplesContextDays,
  NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS,
} from "./nwssWeeklyCharts"
import {
  CompactTab,
  CompactTabs,
  DrawerVariantProvider,
  precomputedDrawerPaperSx,
  summaryChipSx,
  type DrawerVariant,
} from "./precomputedDrawerStyles"
import {
  formatNwssResolvedLocation,
  formatNwssSignalDescription,
} from "./nwssDisplayFormat"
import {
  NWSS_TAB_BY_COUNTY,
  NWSS_TAB_BY_SITE,
  NWSS_TAB_RAW_DATA,
  nwssWeeklyByCountyTitle,
} from "./nwssDrawerScope"
import type { PrecomputedBiothreatSignal } from "./types"
import {
  NwssHistoricViewFilterToggle,
  type NwssHistoricViewFilter,
} from "./NwssHistoricViewFilterToggle"
import { NwssSampleScopeToggle } from "./NwssSampleScopeToggle"
import {
  applyNwssSampleScope,
  deriveNwssSamplesViews,
  useNwssSignalSamples,
  type NwssDrawerSampleScope,
  type NwssSignalSamplesResult,
} from "./useNwssSignalSamples"

const DRAWER_WIDTH = 840

type StatusKey = "start" | "ongoing" | "ended"

const STATUS_STYLE: Record<
  StatusKey,
  { bg: string; border: string; color: string; label: string }
> = {
  start: {
    bg: extraColors.status.greenDeep,
    border: extraColors.status.greenMuted,
    color: extraColors.status.greenLight,
    label: "New",
  },
  ongoing: {
    bg: extraColors.status.yellowDeep,
    border: extraColors.status.yellowMuted,
    color: extraColors.status.yellowLight,
    label: "Ongoing",
  },
  ended: {
    bg: extraColors.dark5,
    border: extraColors.dark10,
    color: extraColors.medium,
    label: "Ended",
  },
}

type NwssDrawerTab = "raw" | "counties" | "sites"

interface NwssSignalDrawerProps {
  signal: PrecomputedBiothreatSignal | null
  onClose: () => void
  /** "command" applies the v2 console treatment to the drawer. */
  variant?: DrawerVariant
}

export const NwssSignalDrawer: React.FC<NwssSignalDrawerProps> = ({
  signal,
  onClose,
  variant = "default",
}) => (
  <Drawer
    anchor="right"
    variant="persistent"
    open={signal !== null}
    onClose={onClose}
    slotProps={{
      paper: {
        sx: {
          ...precomputedDrawerPaperSx(DRAWER_WIDTH, variant),
          maxWidth: "92vw",
        },
      },
    }}
  >
    {signal && (
      <DrawerVariantProvider value={variant}>
        <NwssDrawerHeader
          signal={signal}
          onClose={onClose}
          detailHref={buildBiothreatSignalDetailPath("nwss", signal.cluster_id)}
        />
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <NwssSamplesSection key={signal.cluster_id} signal={signal} />
        </Box>
      </DrawerVariantProvider>
    )}
  </Drawer>
)

interface NwssDrawerHeaderProps {
  signal: PrecomputedBiothreatSignal
  /** Omit on the standalone detail page (no drawer to close). */
  onClose?: () => void
  /** When set, the header shows an "Open full page" link (drawer only). */
  detailHref?: string
}

export const NwssDrawerHeader: React.FC<NwssDrawerHeaderProps> = ({
  signal,
  onClose,
  detailHref,
}) => (
  <>
    <NwssDrawerTitleBar
      title={formatNwssSignalDescription(signal.title) || "Wastewater signal"}
      onClose={onClose}
      detailHref={detailHref}
    />
    <NwssSummaryChips signal={signal} />
    <NwssActivityDates signal={signal} />
    <Divider sx={{ borderColor: extraColors.translucent5, mb: 2 }} />
  </>
)

interface NwssDrawerTitleBarProps {
  title: string
  onClose?: () => void
  detailHref?: string
}

const NwssDrawerTitleBar: React.FC<NwssDrawerTitleBarProps> = ({
  title,
  onClose,
  detailHref,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 1,
      mb: 2,
    }}
  >
    <Typography variant="h3Bold" sx={{ lineHeight: 1.3 }}>
      {title}
    </Typography>
    <Stack direction="row" spacing={0.5} sx={{ mt: "-4px", mr: "-4px" }}>
      {detailHref && (
        <IconButton
          component={RouterLink}
          to={detailHref}
          size="small"
          aria-label="Open full page"
          sx={{ color: extraColors.white }}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      )}
      {onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close drawer"
          sx={{ color: extraColors.white }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Stack>
  </Box>
)

interface NwssSummaryChipsProps {
  signal: PrecomputedBiothreatSignal
}

const NwssSummaryChips: React.FC<NwssSummaryChipsProps> = ({ signal }) => {
  const meta = signal.nwss_meta
  const status = (meta?.status ?? null) as StatusKey | null
  const displayLocation = formatNwssResolvedLocation(signal.resolved_location)
  const locationLabel = meta?.aggregation
    ? `${displayLocation} · ${meta.aggregation}`
    : displayLocation
  const diseaseCodes = signal.clean_disease_codes.length
    ? signal.clean_disease_codes
    : meta?.disease
      ? [meta.disease]
      : []

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{ flexWrap: "wrap", alignItems: "center", mb: 1.5 }}
    >
      {status && <NwssStatusBadge status={status} />}
      {diseaseCodes.map(code => (
        <Chip
          key={code}
          size="small"
          variant="outlined"
          label={code.replace(/_/g, " ")}
          sx={summaryChipSx}
        />
      ))}
      {signal.resolved_location && (
        <Chip
          size="small"
          variant="outlined"
          label={locationLabel}
          sx={summaryChipSx}
        />
      )}
    </Stack>
  )
}

interface NwssStatusBadgeProps {
  status: StatusKey
}

const NwssStatusBadge: React.FC<NwssStatusBadgeProps> = ({ status }) => {
  const style = STATUS_STYLE[status]
  return (
    <Box
      sx={{
        px: 1,
        py: "2px",
        borderRadius: "4px",
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
        color: style.color,
        fontSize: "0.65rem",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {style.label}
    </Box>
  )
}

interface NwssActivityDatesProps {
  signal: PrecomputedBiothreatSignal
}

const NwssActivityDates: React.FC<NwssActivityDatesProps> = ({ signal }) => {
  const meta = signal.nwss_meta
  if (!meta) return null
  return (
    <Typography
      variant="caption"
      sx={{ color: extraColors.translucent50, display: "block", mb: 2 }}
    >
      {`Active ${formatNwssActiveDateRange(signal)} · ${meta.window_days}d window`}
    </Typography>
  )
}

interface NwssSamplesSectionProps {
  signal: PrecomputedBiothreatSignal
}

export const NwssSamplesSection: React.FC<NwssSamplesSectionProps> = ({
  signal,
}) => {
  const isStateEvent = signal.nwss_meta?.aggregation === "state"
  const isCountyEvent = signal.nwss_meta?.aggregation === "county"
  const showCountyTab = isStateEvent || isCountyEvent
  const reportingCounties = useMemo(
    () =>
      showCountyTab && signal.nwss_meta?.counties?.length
        ? signal.nwss_meta.counties
        : undefined,
    [showCountyTab, signal.nwss_meta?.counties]
  )
  const contextDays = useMemo(
    () => computeNwssSamplesContextDays(signal.nwss_meta),
    [signal.nwss_meta]
  )
  const resolvedGeo = signal.resolved_location
  const result = useNwssSignalSamples(signal.cluster_id, {
    reportingCounties,
    contextDays,
    resolvedGeo,
  })
  const [sampleScope, setSampleScope] =
    useState<NwssDrawerSampleScope>("historic")
  const [tab, setTab] = useState<NwssDrawerTab>(
    showCountyTab ? "counties" : "sites"
  )

  const displayResult = useMemo(
    () => applyNwssSampleScope(result, sampleScope, resolvedGeo),
    [result, sampleScope, resolvedGeo]
  )
  const tabs: { id: NwssDrawerTab; label: string }[] = showCountyTab
    ? [
        { id: "counties", label: NWSS_TAB_BY_COUNTY },
        { id: "sites", label: NWSS_TAB_BY_SITE },
        { id: "raw", label: NWSS_TAB_RAW_DATA },
      ]
    : [
        { id: "sites", label: NWSS_TAB_BY_SITE },
        { id: "raw", label: NWSS_TAB_RAW_DATA },
      ]

  if (result.isLoading) return <NwssSamplesLoading />
  if (result.isError) return <NwssSamplesError />
  if (result.samples.length === 0) return <NwssSamplesEmpty />

  return (
    <Stack spacing={2}>
      <Stack spacing={3}>
        <NwssStatBand result={displayResult} sampleScope={sampleScope} />
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <NwssSampleScopeToggle
            scope={sampleScope}
            onScopeChange={setSampleScope}
          />
        </Box>
      </Stack>
      {sampleScope === "event" && displayResult.samples.length === 0 && (
        <Typography variant="caption" sx={{ color: extraColors.hint }}>
          No event detections in the loaded sample history. Switch to the
          Historic scope to see the full sampling context.
        </Typography>
      )}
      {result.truncated && (
        <NwssTruncationBanner sampleCount={result.samples.length} />
      )}
      <NwssDrawerTabs tabs={tabs} activeTab={tab} onTabChange={setTab} />
      <NwssDrawerTabPanel
        tab={tab}
        signal={signal}
        result={displayResult}
        isStateEvent={isStateEvent}
        showCountyTab={showCountyTab}
        sampleScope={sampleScope}
      />
    </Stack>
  )
}

const NwssSamplesLoading: React.FC = () => (
  <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
    <CircularProgress size={24} />
  </Box>
)

const NwssSamplesError: React.FC = () => (
  <Typography
    variant="body2"
    sx={{ color: extraColors.status.redLight, textAlign: "center", pt: 4 }}
  >
    Failed to load wastewater samples.
  </Typography>
)

const NwssSamplesEmpty: React.FC = () => (
  <Typography
    variant="body2"
    sx={{ color: extraColors.hint, textAlign: "center", pt: 4 }}
  >
    No raw samples found for this signal.
  </Typography>
)

interface NwssTruncationBannerProps {
  sampleCount: number
}

const NwssTruncationBanner: React.FC<NwssTruncationBannerProps> = ({
  sampleCount,
}) => (
  <Typography variant="caption" sx={{ color: extraColors.status.yellowLight }}>
    Showing the first {sampleCount.toLocaleString()} samples — this event is
    large, so the view is truncated.
  </Typography>
)

interface NwssDrawerTabsProps {
  tabs: { id: NwssDrawerTab; label: string }[]
  activeTab: NwssDrawerTab
  onTabChange: (tab: NwssDrawerTab) => void
}

const NwssDrawerTabs: React.FC<NwssDrawerTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
}) => (
  <CompactTabs
    value={tabs.findIndex(entry => entry.id === activeTab)}
    onChange={(_event: SyntheticEvent, index: number) => {
      const next = tabs[index]
      if (next) onTabChange(next.id)
    }}
    slotProps={{ indicator: { sx: { height: 2 } } }}
  >
    {tabs.map(entry => (
      <CompactTab key={entry.id} label={entry.label} />
    ))}
  </CompactTabs>
)

interface NwssDrawerTabPanelProps {
  tab: NwssDrawerTab
  signal: PrecomputedBiothreatSignal
  result: NwssSignalSamplesResult
  isStateEvent: boolean
  showCountyTab: boolean
  sampleScope: NwssDrawerSampleScope
}

const NwssDrawerTabPanel: React.FC<NwssDrawerTabPanelProps> = ({
  tab,
  signal,
  result,
  isStateEvent,
  showCountyTab,
  sampleScope,
}) => {
  if (sampleScope === "event" && result.samples.length === 0) {
    return null
  }

  switch (tab) {
    case "raw":
      return (
        <NwssRawTable
          samples={result.samples}
          showCountyColumn={showCountyTab}
          showEventDetectionsFilter={showCountyTab}
          reportingCounties={result.reportingCounties}
          sampleScope={sampleScope}
        />
      )
    case "counties":
      return (
        <NwssCountyDetectionsTab
          signal={signal}
          result={result}
          sampleScope={sampleScope}
        />
      )
    case "sites":
      return (
        <NwssSiteDetectionsTab
          signal={signal}
          result={result}
          isStateEvent={isStateEvent}
          sampleScope={sampleScope}
        />
      )
  }
}

interface NwssCountyDetectionsTabProps {
  signal: PrecomputedBiothreatSignal
  result: NwssSignalSamplesResult
  sampleScope: NwssDrawerSampleScope
}

const NwssCountyDetectionsTab: React.FC<NwssCountyDetectionsTabProps> = ({
  signal,
  result,
  sampleScope,
}) => {
  const [viewFilter, setViewFilter] = useState<NwssHistoricViewFilter>("all")
  const resolvedGeo = signal.resolved_location

  const { tabSamples, chartScope, countyViews } = useMemo(() => {
    const eventOnly = sampleScope === "event" || viewFilter === "event"
    const scopedSamples = eventOnly
      ? result.samples.filter(sample => sample.is_event_member)
      : result.samples
    const scope: NwssDrawerSampleScope = eventOnly ? "event" : "historic"

    return {
      tabSamples: scopedSamples,
      chartScope: scope,
      countyViews: deriveNwssSamplesViews(scopedSamples, {
        reportingCounties: result.reportingCounties,
        resolvedGeo,
        scope,
      }),
    }
  }, [result, viewFilter, sampleScope, resolvedGeo])

  return (
    <Stack spacing={4}>
      {sampleScope === "historic" && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <NwssHistoricViewFilterToggle
            value={viewFilter}
            onChange={setViewFilter}
          />
        </Box>
      )}
      <NwssRollingDetectionRateChart
        panels={countyViews.countyRollingRates}
        samples={tabSamples}
        disease={signal.nwss_meta?.disease}
        truncated={result.truncated}
        reportingCounties={result.reportingCounties}
        sampleScope={chartScope}
      />
      <Stack spacing="64px">
        <NwssDetectionHeatmap
          heatmap={countyViews.countyHeatmap}
          samples={tabSamples}
          groupBy="county"
          rowNoun="County"
          sectionTitle={nwssWeeklyByCountyTitle(chartScope)}
          reportingCounties={result.reportingCounties}
          sampleScope={chartScope}
          eventWindow={
            signal.nwss_meta?.start_date && signal.nwss_meta.end_date
              ? {
                  startDate: signal.nwss_meta.start_date,
                  endDate: signal.nwss_meta.end_date,
                }
              : null
          }
        />
        <NwssEntityBreakdown
          entityLabel="County"
          sampleScope={chartScope}
          summaries={countyViews.countySummaries.map(summary => ({
            key: summary.county,
            sampleCount: summary.sampleCount,
            detectionCount: summary.detectionCount,
            latestSampleDate: summary.latestSampleDate,
            latestDetectionDate: summary.latestDetectionDate,
            daysSinceLastDetection: summary.daysSinceLastDetection,
          }))}
        />
      </Stack>
    </Stack>
  )
}

interface NwssSiteDetectionsTabProps {
  signal: PrecomputedBiothreatSignal
  result: NwssSignalSamplesResult
  isStateEvent: boolean
  sampleScope: NwssDrawerSampleScope
}

const NwssSiteDetectionsTab: React.FC<NwssSiteDetectionsTabProps> = ({
  signal,
  result,
  isStateEvent,
  sampleScope,
}) => (
  <Stack spacing={4}>
    {!isStateEvent && (
      <NwssRollingDetectionRateChart
        panels={result.siteTabRollingRates}
        samples={result.samples}
        disease={signal.nwss_meta?.disease}
        truncated={result.truncated}
        reportingCounties={result.reportingCounties}
        lookbackMonths={NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS}
        sampleScope={sampleScope}
      />
    )}
    <Stack spacing="72px">
      <NwssSiteBubbleTimeline
        timeline={result.siteBubbleTimeline}
        samples={result.samples}
        disease={signal.nwss_meta?.disease}
        geo={signal.resolved_location}
        reportingCounties={result.reportingCounties}
        truncated={result.truncated}
        sampleScope={sampleScope}
        eventWindow={
          signal.nwss_meta?.start_date && signal.nwss_meta.end_date
            ? {
                startDate: signal.nwss_meta.start_date,
                endDate: signal.nwss_meta.end_date,
              }
            : null
        }
      />
      <NwssEntityBreakdown
        entityLabel="Site"
        showCountyColumn
        sampleScope={sampleScope}
        summaries={result.siteSummaries.map(summary => ({
          key: summary.site,
          county: summary.county,
          sampleCount: summary.sampleCount,
          detectionCount: summary.detectionCount,
          latestSampleDate: summary.latestSampleDate,
          latestDetectionDate: summary.latestDetectionDate,
          daysSinceLastDetection: summary.daysSinceLastDetection,
        }))}
      />
    </Stack>
  </Stack>
)
